'use client';

import deepmerge from 'deepmerge';
import { createContext, useCallback, useMemo, useState } from 'react';
import type React from 'react';
import { VanityUrlWithPath } from '~/contracts/srp';
import type { DeepPartial } from '~/types';

export type ContextType = {
  isOpenSearch: boolean;
  setChanged: () => void;
  vanityUrl?: VanityUrlWithPath;
};

const defaultContext: ContextType = {
  isOpenSearch: false,
  setChanged() {},
  vanityUrl: undefined
};

const SEOContext = createContext<ContextType>(defaultContext);

export type OwnProps = React.PropsWithChildren<{
  isOpenSearch: boolean;
  vanityUrl?: VanityUrlWithPath;
}>;

export function SEOContextProvider(props: OwnProps) {
  const [didChange, setDidChange] = useState(false);
  const setChanged = useCallback(() => {
    setDidChange(true);
  }, []);

  const value = useMemo<ContextType>(
    () => ({
      isOpenSearch: props.isOpenSearch,
      setChanged,
      vanityUrl: didChange ? undefined : props.vanityUrl
    }),
    [didChange, props.isOpenSearch, props.vanityUrl, setChanged]
  );

  return (
    <SEOContext.Provider value={value}>{props.children}</SEOContext.Provider>
  );
}

SEOContextProvider.displayName = 'SEOContextProvider';

export const generateSEOContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function MockedSEOContextProvider({
  children,
  mockValue
}: React.PropsWithChildren<{ mockValue: DeepPartial<ContextType> }>) {
  return (
    <SEOContext.Provider value={generateSEOContext(mockValue)}>
      {children}
    </SEOContext.Provider>
  );
}

export default SEOContext;
